import React, { useState } from 'react';
import { useContactInfo } from '../../hooks/useAdminData';
import { FaPhone, FaEnvelope, FaClock, FaMapMarkerAlt } from 'react-icons/fa';
import './Contact.scss';

// Vercel API endpoint for sending booking emails
// Using production alias URL (more stable than deployment-specific URLs)
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || '/api/send-email';

// SMS consent text shown next to the checkbox (also sent with the booking as proof of consent)
export const SMS_CONSENT_TEXT =
  'By entering your mobile number, you consent to receive text messages and emails from Premium Pets Mobile Grooming related to your inquiry about our services, products, and support. Message frequency may vary. Message and data rates may apply. Text HELP for help, STOP to stop.';

// Log API endpoint in development
if (process.env.NODE_ENV === 'development') {
  console.log('API Endpoint:', API_ENDPOINT);
}

const Contact: React.FC = () => {
  const contactInfo = useContactInfo();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    petName: '',
    preferredDate: '',
    petBreed: '',
    serviceType: '',
    petWeight: '',
    comments: '',
    smsConsent: false
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showBreedDropdown, setShowBreedDropdown] = useState(false);
  const [filteredBreeds, setFilteredBreeds] = useState<string[]>([]);

  // Comprehensive list of dog breeds
  const dogBreeds = [
    'Affenpinscher', 'Afghan Hound', 'Airedale Terrier', 'Akita', 'Alaskan Malamute', 'American Bulldog', 'American Eskimo Dog', 'American Foxhound', 'American Pit Bull Terrier', 'American Staffordshire Terrier', 'American Water Spaniel', 'Anatolian Shepherd Dog', 'Australian Cattle Dog', 'Australian Shepherd', 'Australian Terrier', 'Basenji', 'Basset Hound', 'Beagle', 'Bearded Collie', 'Bedlington Terrier', 'Belgian Malinois', 'Belgian Sheepdog', 'Belgian Tervuren', 'Bernese Mountain Dog', 'Bichon Frise', 'Black and Tan Coonhound', 'Bloodhound', 'Border Collie', 'Border Terrier', 'Borzoi', 'Boston Terrier', 'Bouvier des Flandres', 'Boxer', 'Briard', 'Brittany', 'Brussels Griffon', 'Bull Terrier', 'Bulldog', 'Bullmastiff', 'Cairn Terrier', 'Canaan Dog', 'Cardigan Welsh Corgi', 'Cavalier King Charles Spaniel', 'Chesapeake Bay Retriever', 'Chihuahua', 'Chinese Crested', 'Chinese Shar-Pei', 'Chow Chow', 'Clumber Spaniel', 'Cocker Spaniel', 'Collie', 'Coonhound', 'Corgi', 'Coton de Tulear', 'Curly-Coated Retriever', 'Dachshund', 'Dalmatian', 'Dandie Dinmont Terrier', 'Doberman Pinscher', 'English Bulldog', 'English Cocker Spaniel', 'English Foxhound', 'English Setter', 'English Springer Spaniel', 'English Toy Spaniel', 'Field Spaniel', 'Finnish Spitz', 'Flat-Coated Retriever', 'Fox Terrier', 'Foxhound', 'French Bulldog', 'German Pinscher', 'German Shepherd Dog', 'German Shorthaired Pointer', 'German Wirehaired Pointer', 'Giant Schnauzer', 'Glen of Imaal Terrier', 'Golden Retriever', 'Gordon Setter', 'Great Dane', 'Great Pyrenees', 'Greater Swiss Mountain Dog', 'Greyhound', 'Harrier', 'Havanese', 'Ibizan Hound', 'Icelandic Sheepdog', 'Irish Red and White Setter', 'Irish Setter', 'Irish Terrier', 'Irish Water Spaniel', 'Irish Wolfhound', 'Italian Greyhound', 'Jack Russell Terrier', 'Japanese Chin', 'Keeshond', 'Kerry Blue Terrier', 'Komondor', 'Kuvasz', 'Labrador Retriever', 'Lakeland Terrier', 'Leonberger', 'Lhasa Apso', 'Lowchen', 'Maltese', 'Manchester Terrier', 'Mastiff', 'Miniature Bull Terrier', 'Miniature Pinscher', 'Miniature Schnauzer', 'Mixed Breed', 'Neapolitan Mastiff', 'Newfoundland', 'Norfolk Terrier', 'Norwegian Elkhound', 'Norwich Terrier', 'Nova Scotia Duck Tolling Retriever', 'Old English Sheepdog', 'Otterhound', 'Papillon', 'Parson Russell Terrier', 'Pekingese', 'Pembroke Welsh Corgi', 'Petit Basset Griffon Vendeen', 'Pharaoh Hound', 'Pointer', 'Pomeranian', 'Poodle', 'Portuguese Water Dog', 'Pug', 'Puli', 'Pyrenean Shepherd', 'Redbone Coonhound', 'Rhodesian Ridgeback', 'Rottweiler', 'Saint Bernard', 'Saluki', 'Samoyed', 'Schipperke', 'Scottish Deerhound', 'Scottish Terrier', 'Sealyham Terrier', 'Shetland Sheepdog', 'Shiba Inu', 'Shih Tzu', 'Siberian Husky', 'Silky Terrier', 'Skye Terrier', 'Smooth Fox Terrier', 'Soft Coated Wheaten Terrier', 'Spinone Italiano', 'Staffordshire Bull Terrier', 'Standard Schnauzer', 'Sussex Spaniel', 'Tibetan Mastiff', 'Tibetan Spaniel', 'Tibetan Terrier', 'Toy Fox Terrier', 'Treeing Walker Coonhound', 'Vizsla', 'Weimaraner', 'Welsh Springer Spaniel', 'Welsh Terrier', 'West Highland White Terrier', 'Whippet', 'Wire Fox Terrier', 'Wirehaired Pointing Griffon', 'Yorkshire Terrier'
  ];

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation (optional; required only to receive SMS)
    if (formData.phone.trim() && !/^[\d\s\-+()]{10,}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    } else if (formData.smsConsent && !formData.phone.trim()) {
      newErrors.phone = 'Please enter your phone number to receive text messages';
    }

    // Address validation
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    // Pet Name validation
    if (!formData.petName.trim()) {
      newErrors.petName = 'Pet name is required';
    }

    // Preferred Date validation
    if (!formData.preferredDate) {
      newErrors.preferredDate = 'Preferred date is required';
    } else {
      const selectedDateString = formData.preferredDate; // YYYY-MM-DD format from input
      const todayString = new Date().toISOString().split('T')[0]; // Get today in YYYY-MM-DD format
      
      if (selectedDateString < todayString) {
        newErrors.preferredDate = 'Please select today or a future date';
      }
    }

    // Pet Breed validation
    if (!formData.petBreed.trim()) {
      newErrors.petBreed = 'Pet breed is required';
    }

    // Service Type validation
    if (!formData.serviceType) {
      newErrors.serviceType = 'Please select a service type';
    }

    // Pet weight validation (lbs) - acceptable range for dogs
    const weightStr = formData.petWeight.trim();
    if (!weightStr) {
      newErrors.petWeight = 'Please enter pet weight (lbs)';
    } else {
      const weight = parseInt(weightStr, 10);
      if (isNaN(weight) || weight < 1 || weight > 150) {
        newErrors.petWeight = 'Please enter a valid weight (1-150 lbs)';
      }
    }

    // Comments/Special Requests is optional - no validation needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setFormData(prev => ({ ...prev, smsConsent: checked }));
    if (!checked && errors.phone) {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Handle breed autocomplete
    if (name === 'petBreed') {
      const filtered = dogBreeds.filter(breed => 
        breed.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredBreeds(filtered);
      setShowBreedDropdown(value.length > 0 && filtered.length > 0);
    }

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBreedSelect = (breed: string) => {
    setFormData(prev => ({
      ...prev,
      petBreed: breed
    }));
    setShowBreedDropdown(false);
    setFilteredBreeds([]);
    
    // Clear error if exists
    if (errors.petBreed) {
      setErrors(prev => ({
        ...prev,
        petBreed: ''
      }));
    }
  };

  const handleBreedFocus = () => {
    if (formData.petBreed.length > 0) {
      const filtered = dogBreeds.filter(breed => 
        breed.toLowerCase().includes(formData.petBreed.toLowerCase())
      );
      setFilteredBreeds(filtered);
      setShowBreedDropdown(filtered.length > 0);
    }
  };

  const handleBreedBlur = () => {
    // Delay hiding dropdown to allow for clicks
    setTimeout(() => {
      setShowBreedDropdown(false);
    }, 200);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Prepare form data - ensure all fields are properly formatted
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        petName: formData.petName.trim(),
        preferredDate: formData.preferredDate,
        petBreed: formData.petBreed.trim(),
        serviceType: formData.serviceType,
        petSize: formData.petWeight.trim() + 'lb',
        comments: formData.comments ? formData.comments.trim() : '',
        smsConsent: formData.smsConsent,
        smsConsentAt: formData.smsConsent ? new Date().toISOString() : null,
        smsConsentText: formData.smsConsent ? SMS_CONSENT_TEXT : null
      };

      console.log('Sending booking request to:', API_ENDPOINT);
      console.log('Payload:', payload);

      // Send booking request to Vercel API endpoint
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      // Check if response is ok
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `HTTP ${response.status}: ${response.statusText}` }));
        throw new Error(errorData.message || errorData.error || `Server returned ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          petName: '',
          preferredDate: '',
          petBreed: '',
          serviceType: '',
          petWeight: '',
          comments: '',
          smsConsent: false
        });
      } else {
        console.error('API error:', result);
        setSubmitStatus('error');
      }
    } catch (error: any) {
      console.error('Error sending booking request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <div className="contact-header">
          <h2 className="contact-title">
            <span className="highlight-text">BOOK YOUR</span> APPOINTMENT
          </h2>
          <p className="contact-subtitle">
            Ready to give your furry friend the royal treatment? Let's schedule a spa day!
          </p>
        </div>
        
        <div className="contact-content">
          {/* Appointment Form */}
          <div className="form-section">
            <h3 className="form-title">Schedule Now</h3>
            <form onSubmit={handleSubmit} className="appointment-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Name</label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`form-input ${errors.name ? 'error' : ''}`}
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email</label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`form-input ${errors.email ? 'error' : ''}`}
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Mobile Phone (Optional)</label>
                  <input
                    id="phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`form-input ${errors.phone ? 'error' : ''}`}
                  />
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="petName" className="form-label">Pet Name</label>
                  <input
                    id="petName"
                    type="text"
                    name="petName"
                    value={formData.petName}
                    onChange={handleInputChange}
                    className={`form-input ${errors.petName ? 'error' : ''}`}
                  />
                  {errors.petName && <span className="error-message">{errors.petName}</span>}
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="address" className="form-label">Address</label>
                <input
                  id="address"
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={`form-input ${errors.address ? 'error' : ''}`}
                  placeholder="Enter your service address"
                />
                {errors.address && <span className="error-message">{errors.address}</span>}
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="preferredDate" className="form-label">Preferred Date</label>
                  <input
                    id="preferredDate"
                    type="date"
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleInputChange}
                    className={`form-input ${errors.preferredDate ? 'error' : ''}`}
                  />
                  {errors.preferredDate && <span className="error-message">{errors.preferredDate}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="petBreed" className="form-label">Pet Breed</label>
                  <div className="autocomplete-container">
                    <input
                      id="petBreed"
                      type="text"
                      name="petBreed"
                      value={formData.petBreed}
                      onChange={handleInputChange}
                      onFocus={handleBreedFocus}
                      onBlur={handleBreedBlur}
                      className={`form-input ${errors.petBreed ? 'error' : ''}`}
                      placeholder="Start typing to search breeds..."
                      autoComplete="off"
                    />
                    {showBreedDropdown && (
                      <div className="autocomplete-dropdown">
                        {filteredBreeds.slice(0, 10).map((breed, index) => (
                          <div
                            key={index}
                            className="autocomplete-item"
                            onClick={() => handleBreedSelect(breed)}
                          >
                            {breed}
                          </div>
                        ))}
                        {filteredBreeds.length > 10 && (
                          <div className="autocomplete-item more-results">
                            +{filteredBreeds.length - 10} more breeds...
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {errors.petBreed && <span className="error-message">{errors.petBreed}</span>}
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="petWeight" className="form-label">Pet weight (lbs)</label>
                  <input
                    type="number"
                    id="petWeight"
                    name="petWeight"
                    value={formData.petWeight}
                    onChange={handleInputChange}
                    placeholder="e.g. 23, 68"
                    min={1}
                    max={150}
                    className={`form-input ${errors.petWeight ? 'error' : ''}`}
                  />
                  {errors.petWeight && <span className="error-message">{errors.petWeight}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="serviceType" className="form-label">Service Type</label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleInputChange}
                    className={`form-input ${errors.serviceType ? 'error' : ''}`}
                  >
                    <option value="">Select Service</option>
                    <option value="5-Star Bath">5-Star Bath</option>
                    <option value="Mini-Groom">Mini-Groom</option>
                    <option value="Full Groom">Full Groom</option>
                  </select>
                  {errors.serviceType && <span className="error-message">{errors.serviceType}</span>}
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="comments" className="form-label">Special Requests or Notes (Optional)</label>
                <textarea
                  id="comments"
                  name="comments"
                  value={formData.comments}
                  onChange={handleInputChange}
                  rows={4}
                  className="form-textarea"
                  placeholder="Any special requests or notes about your pet..."
                />
              </div>
              
              <div className="form-group sms-consent">
                <label htmlFor="smsConsent" className="sms-consent-label">
                  <input
                    id="smsConsent"
                    type="checkbox"
                    name="smsConsent"
                    checked={formData.smsConsent}
                    onChange={handleConsentChange}
                    className="sms-consent-checkbox"
                  />
                  <span>
                    <strong>(Optional)</strong> {SMS_CONSENT_TEXT}{' '}
                    Read our <a href="/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a> for more information.
                  </span>
                </label>
              </div>

              {submitStatus === 'success' && (
                <div className="success-message" style={{ padding: '15px', backgroundColor: '#efe', border: '1px solid #cfc', borderRadius: '5px', color: '#3c3' }}>
                  ✅ Your booking request has been sent successfully! We'll contact you soon to confirm your appointment.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="error-message" style={{ padding: '15px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '5px', color: '#c33' }}>
                  ❌ There was an error sending your request. Please check your connection and try again, or contact us directly at {contactInfo.phone}.
                </div>
              )}

              <button 
                type="submit" 
                className={`submit-btn ${isSubmitting ? 'loading' : ''}`}
                disabled={isSubmitting}
                aria-label="Submit booking request"
              >
                {isSubmitting ? 'Sending...' : 'Send Booking Request'}
              </button>
            </form>
          </div>
          
          {/* Contact Information */}
          <div className="contact-info">
            <div className="contact-card">
              <div className="contact-icon">
                {FaPhone({ size: 24 })}
              </div>
              <div>
                <h3 className="contact-item-title">Call Us</h3>
                <p className="contact-item-text">{contactInfo.phone}</p>
                <p className="contact-item-subtitle">Quick answers</p>
              </div>
            </div>
            
            <div className="contact-card">
              <div className="contact-icon">
                {FaEnvelope({ size: 24 })}
              </div>
              <div>
                <h3 className="contact-item-title">Email Us</h3>
                <p className="contact-item-text">{contactInfo.email}</p>
                <p className="contact-item-subtitle">Fast Replies</p>
              </div>
            </div>
            
            <div className="contact-card">
              <div className="contact-icon">
                {FaClock({ size: 24 })}
              </div>
              <div>
                <h3 className="contact-item-title">Prefer Text Message?</h3>
                <p className="contact-item-text">{contactInfo.phone}</p>
                <p className="contact-item-subtitle">Send us a quick message</p>
              </div>
            </div>
            
            <div className="contact-card">
              <div className="contact-icon">
                {FaMapMarkerAlt({ size: 24 })}
              </div>
              <div>
                <h3 className="contact-item-title">Service Area</h3>
                <p className="contact-item-text">{contactInfo.serviceArea}</p>
                <p className="contact-item-subtitle">25-miles Radius</p>
              </div>
            </div>
            
            {/* Estimated Time Box */}
            <div className="time-box">
              <div className="time-icon">
                {FaClock({ size: 24 })}
              </div>
              <div>
                <h3 className="time-title">OUR HOURS</h3>
                <p className="time-duration">{contactInfo.hours}</p>
                <p className="time-note">{contactInfo.hoursWeekend}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
